"use client";

import { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  FaUserPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaLinkedin,
  FaEnvelope,
  FaPhone,
  FaBriefcase,
} from "react-icons/fa";
import {
  getMentors,
  createMentor,
  updateMentor,
  deleteMentor,
  toggleMentorStatus,
  getMentorRequests,
  getStartupById,
  uploadImage,
} from "../utils/api";

export default function ModernMentors({ mentor, refreshMentors }) {
  const API_BASE =
    process.env.REACT_APP_API_URL || process.env.NEXT_PUBLIC_API_URL || "";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mentors, setMentors] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const modalRef = useRef();
  const previewRef = useRef(null);
  const FALLBACK_IMAGE = "/placeholder.svg";
  const [editingId, setEditingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  // Requests modal state
  const [isRequestsModalOpen, setIsRequestsModalOpen] = useState(false);
  const [requestsForMentor, setRequestsForMentor] = useState(null); // the mentor object
  const [mentorRequests, setMentorRequests] = useState([]); // fetched requests array
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState(null);
  const requestsModalRef = useRef();

  // fetch mentors from backend
  const fetchMentors = async () => {
    try {
      const data = await getMentors();
      setMentors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch mentors:", err);
    }
  };

  const toggleModal = () => {
    setIsModalOpen((v) => !v);
    // revoke any preview object url
    if (previewRef.current) {
      try {
        URL.revokeObjectURL(previewRef.current);
      } catch (e) {}
      previewRef.current = null;
    }
    setPreviewImage(null);
    setSelectedFile(null);
    setError(null);
    setEditingId(null);
  };

  // simple helper to fetch startup name by id (place near other helpers inside the component)
  const fetchStartupName = async (startupId) => {
    if (!startupId) return null;
    try {
      const res = await getStartupById(startupId);
      // backend returns the startup document, try to read common name fields
      return (
        (res && (res.name || res.startupName || (res.data && (res.data.name || res.data.startupName)))) ||
        null
      );
    } catch (err) {
      // silent fail — return null so UI shows fallback
      console.error("Failed to fetch startup name for", startupId, err);
      return null;
    }
  };

  // open requests modal and fetch requests for given mentor
  // open requests modal and fetch requests for given mentor (updated to attach startup_name)
  const openRequestsModal = async (mentorObj) => {
    setRequestsForMentor(mentorObj || null);
    setIsRequestsModalOpen(true);
    setMentorRequests([]);
    setRequestsError(null);
    setRequestsLoading(true);

    try {
      const mentorId = mentorObj._id || mentorObj.id || mentorObj?.uuid;
      const res = await getMentorRequests(mentorId);
      const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : res?.requests || res?.data || [];

      // If backend already provided startup_name use it; otherwise fetch startup name using /api/startups/:id
      const cache = {};
      const requestsWithNames = await Promise.all(
        data.map(async (req) => {
          // prefer any existing name fields if present
          const existingName = req.startup_name || req.startupName || (req.startup && req.startup.name);
          if (existingName) return { ...req, startup_name: existingName };

          const startupId = req.startup_id || req.startup || req.startupId || null;
          if (!startupId) return { ...req, startup_name: null };

          if (cache[startupId]) {
            return { ...req, startup_name: cache[startupId] || null };
          }

          const name = await fetchStartupName(startupId);
          cache[startupId] = name || null;
          return { ...req, startup_name: name || null };
        })
      );

      setMentorRequests(requestsWithNames);
    } catch (err) {
      console.error("Failed to fetch mentor requests:", err);
      setRequestsError("Failed to load requests. Try again.");
    } finally {
      setRequestsLoading(false);
    }
  };

  // close requests modal and cleanup
  const closeRequestsModal = () => {
    setRequestsForMentor(null);
    setMentorRequests([]);
    setRequestsError(null);
    setIsRequestsModalOpen(false);
    setRequestsLoading(false);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await getMentors();
        if (cancelled) return;

        setMentors(Array.isArray(data) ? data : []);
      } catch (err) {
        // eslint-disable-next-line import/no-named-as-default-member
        if (err?.name === "CanceledError") return;
        console.error("Failed to fetch mentors:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [API_BASE]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // revoke old preview if exists
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);

      const objectUrl = URL.createObjectURL(file);
      previewRef.current = objectUrl; // store in ref for cleanup
      setPreviewImage(objectUrl);
      setSelectedFile(file);
    } else {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
      setPreviewImage(null);
      setSelectedFile(null);
    }
  };

  const handleOutsideClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      toggleModal();
    }
  };

  // handle outside click for requests modal
  const handleOutsideClickRequests = (e) => {
    if (requestsModalRef.current && !requestsModalRef.current.contains(e.target)) {
      closeRequestsModal();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const formEl = e.target;

      // Build a plain object from form fields (avoids accidental File blobs)
      const formEntries = Object.fromEntries(new FormData(formEl).entries());

      // If a file was selected, upload it first
      if (selectedFile) {
        const uploadedUrl = await uploadImage(selectedFile);

        // uploadedUrl might be string or object — handle as the api helper returns (your uploadImage returns URL or null)
        if (!uploadedUrl) {
          console.error("Unexpected upload response: no URL returned");
          throw new Error("Image upload returned no URL. Check upload API response.");
        }

        // put returned url into payload
        formEntries.image = uploadedUrl;
      } else {
        // if no file selected, remove any image field to avoid sending empty strings
        // for edits this preserves the existing image on the server
        delete formEntries.image;
      }

      // coerce any boolean/number fields if necessary
      if (formEntries.isActive !== undefined) {
        formEntries.isActive = Number(formEntries.isActive) || 0;
      }

      // Decide between create and update
      let res;
      if (editingId) {
        // Update (PUT)
        res = await updateMentor(editingId, formEntries);
      } else {
        // Create (POST)
        res = await createMentor(formEntries);
      }

      // success -> refresh list and close modal
      // assume helpers throw on error, so if we reached here it's success
      await fetchMentors();

      // reset editing state if we updated
      if (editingId) setEditingId(null);

      // cleanup preview url object (if any) and selected file
      if (previewRef.current) {
        try { URL.revokeObjectURL(previewRef.current); } catch (e) { }
        previewRef.current = null;
      }
      setPreviewImage(null);
      setSelectedFile(null);

      toggleModal();
    } catch (err) {
      console.error("Create/update mentor error (full):", err);

      // Axios-like server response handling if the api helpers rethrow axios errors
      const serverBody = err && err.response && err.response.data;
      const status = err && err.response && err.response.status;

      if (status === 409 || (serverBody && serverBody.code === 11000) || (serverBody && /duplicate/i.test(serverBody.message || ""))) {
        setError("A mentor with that email already exists.");
      } else if (serverBody && serverBody.message) {
        setError(serverBody.message);
      } else if (err && err.message) {
        setError(err.message);
      } else {
        setError("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };


  const handleEdit = (mentor) => {
    // store id so handleSubmit knows this is an update
    setEditingId(mentor._id || mentor.id || null);

    // set preview image (if mentor has one) and clear selectedFile
    const img = mentor.profileImage || mentor.image || null;
    setPreviewImage(img);
    setSelectedFile(null);
    setError(null);

    // open modal
    setIsModalOpen(true);

    // After modal mounts, populate the uncontrolled form inputs by name.
    // Using setTimeout 0 ensures modal is in DOM; it's a lightweight way to set values.
    setTimeout(() => {
      const form = modalRef.current?.querySelector("form");
      if (!form) return;
      const map = {
        name: mentor.name || "",
        email: mentor.email || "",
        number: mentor.number || mentor.phone || "",
        profession: mentor.profession || "",
        linkedinProfile: mentor.linkedinProfile || mentor.linkedin || "",
        expertise: mentor.expertise || "",
      };
      Object.entries(map).forEach(([k, v]) => {
        const input = form.querySelector(`[name="${k}"]`);
        if (input) input.value = v;
      });
    }, 0);
  };

  const handleToggleMentorStatus = async (id, newStatus) => {
    setLoadingId(id);
    try {
      await toggleMentorStatus(id, newStatus);
      // Then refresh or update local state for immediate feedback
      setMentors(prev =>
        prev.map(m =>
          (m._id || m.id) === id ? { ...m, isActive: newStatus, active: newStatus === 1 } : m
        )
      );
    } catch (err) {
      console.error("Failed to toggle mentor status:", err);
    } finally {
      setLoadingId(null);
    }
  };


  const handleDelete = async (id) => {
    // optional: add delete functionality with confirmation
    if (!confirm("Delete this mentor?")) return;
    try {
      await deleteMentor(id);
      await fetchMentors();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete mentor");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/20 to-blue-100/30"></div>
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-emerald-200/20 to-transparent rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-blue-200/20 to-transparent rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="transform perspective-1000 hover:rotateX-2 transition-transform duration-300">
              <h1 className="text-3xl font-bold text-slate-900 mb-2 drop-shadow-sm">Mentors</h1>
              <p className="text-slate-600">Manage your mentorship network and expertise</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleModal}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 hover:scale-105 active:scale-95 relative overflow-hidden group"
                style={{
                  boxShadow:
                    "0 10px 25px -5px rgba(16, 185, 129, 0.3), 0 4px 6px -2px rgba(16, 185, 129, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <FaUserPlus className="text-sm relative z-10" />
                <span className="relative z-10">Add Mentor</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Total Mentors</p>
                <p className="text-2xl font-bold text-slate-900 drop-shadow-sm">{mentors.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center shadow-lg">
                <FaUserPlus className="text-emerald-600 drop-shadow-sm" />
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Active Mentors</p>
                <p className="text-2xl font-bold text-slate-900 drop-shadow-sm">
                  {mentors.filter((m) => m.active).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center shadow-lg">
                <div className="w-3 h-3 bg-green-500 rounded-full shadow-sm"></div>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Expertise Areas</p>
                <p className="text-2xl font-bold text-slate-900 drop-shadow-sm">
                  {new Set(mentors.map((m) => m.expertise)).size}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center shadow-lg">
                <FaBriefcase className="text-amber-600 drop-shadow-sm" />
              </div>
            </div>
          </div>
        </div>

        {/* Mentors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {mentors.map((mentor) => (
            <div key={mentor._id || mentor.id} className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-white/60 transition-all duration-500 hover:-translate-y-3 hover:rotate-1 transform-gpu group">
              <div className="flex items-center justify-between mb-4">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${mentor.active ? "bg-gradient-to-r from-green-100 to-green-200 text-green-800" : "bg-gradient-to-r from-slate-100 to-slate-200 text-slate-600"}`}
                >
                  <div className={`w-2 h-2 rounded-full mr-2 ${mentor.active ? "bg-green-500" : "bg-slate-400"}`}></div>
                  {mentor.active ? "Active" : "Inactive"}
                </span>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={mentor.isActive === 1}
                    onChange={() => handleToggleMentorStatus(mentor._id || mentor.id, mentor.isActive === 1 ? 0 : 1)}
                    disabled={loadingId === mentor._id || loadingId === mentor.id}
                  />
                  <div
                    className={`w-11 h-6 flex items-center rounded-full p-1 duration-300 ease-in-out
      ${mentor.isActive === 1
                        ? "bg-emerald-500"
                        : "bg-gray-300"}`
                    }
                  >
                    <div
                      className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out
        ${mentor.isActive === 1 ? "translate-x-5" : "translate-x-0"}`
                      }
                    ></div>
                  </div>
                  {loadingId === (mentor._id || mentor.id) && (
                    <span className="ml-2 text-sm text-gray-500">Updating...</span>
                  )}
                </label>

              </div>

              <div className="flex items-center gap-4 mb-4">
                <div className="relative">
                  <img
                    src={mentor.profileImage || FALLBACK_IMAGE}
                    alt={mentor.name}
                    className="w-16 h-16 rounded-xl object-cover border-2 border-white shadow-lg"
                    onError={(e) => {
                      // fallback if URL is invalid or fails to load
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 text-lg">{mentor.name}</h3>
                  <p className="text-slate-600 text-sm">{mentor.profession}</p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <FaEnvelope className="text-xs" />
                  <span className="truncate">{mentor.email}</span>
                </div>
                {mentor.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <FaPhone className="text-xs" />
                    <span>{mentor.phone}</span>
                  </div>
                )}
                {mentor.linkedin && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <FaLinkedin className="text-xs" />
                    <a href={mentor.linkedin} target="_blank" rel="noreferrer" className="truncate underline text-slate-700">LinkedIn Profile</a>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700">{mentor.expertise}</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => openRequestsModal(mentor)}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg text-sm font-medium"
                >
                  View Requests
                </button>
                <button
                  onClick={() => handleEdit(mentor)}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-lg text-sm font-medium"
                >
                  <FaEdit className="text-xs" /> Edit
                </button>
                <button onClick={() => handleDelete(mentor._id || mentor.id)} className="flex-1 inline-flex items-center justify-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm font-medium">
                  <FaTrash className="text-xs" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {mentors.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaUserPlus className="text-slate-400 text-2xl" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No mentors yet</h3>
            <p className="text-slate-600 mb-6">Get started by adding your first mentor to the network.</p>
            <button onClick={toggleModal} className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-3 rounded-xl">
              <FaUserPlus /> Add Your First Mentor
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex justify-center items-center z-50 p-4" onClick={handleOutsideClick}>
          <div ref={modalRef} className="bg-white/95 backdrop-blur-sm rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200/50">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Add New Mentor</h3>
                <p className="text-slate-600 text-sm mt-1">Fill in the details to add a mentor to your network</p>
              </div>
              <button onClick={toggleModal} className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500">
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              {/* Profile Image Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-3">Profile Image</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                    {previewImage ? (
                      <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <FaUserPlus className="text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input name="image" type="file" accept="image/*" onChange={handleImageChange} className="block w-full text-sm text-slate-500" />
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 10MB</p>
                    {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                  <input name="name" type="text" required className="w-full px-4 py-3 border border-slate-300 rounded-xl" placeholder="Enter full name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                  <input name="email" type="email" required className="w-full px-4 py-3 border border-slate-300 rounded-xl" placeholder="Enter email address" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                  <input name="number" type="tel" required className="w-full px-4 py-3 border border-slate-300 rounded-xl" placeholder="+1 (555) 123-4567" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Profession *</label>
                  <input name="profession" type="text" required className="w-full px-4 py-3 border border-slate-300 rounded-xl" placeholder="e.g., Senior Developer" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">LinkedIn Profile</label>
                  <input name="linkedinProfile" type="url" className="w-full px-4 py-3 border border-slate-300 rounded-xl" placeholder="https://linkedin.com/in/username" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Area of Expertise *</label>
                  <input name="expertise" type="text" required className="w-full px-4 py-3 border border-slate-300 rounded-xl" placeholder="e.g., Web Development" />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-200/50">
                <button type="button" onClick={toggleModal} className="px-6 py-3 border border-slate-300 text-slate-700 rounded-xl">Cancel</button>
                <button type="submit" disabled={loading} className="px-6 py-3 bg-emerald-600 text-white rounded-xl">
                  {loading ? "Saving..." : "Add Mentor"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Requests Modal - dynamic fetch */}
      {isRequestsModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex justify-center items-start z-50 p-6 pt-24" onClick={handleOutsideClickRequests}>
          <div ref={requestsModalRef} className="bg-white/95 backdrop-blur-sm rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-slate-200/50">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Mentor Requests {requestsForMentor ? `— ${requestsForMentor.name || requestsForMentor.email || requestsForMentor._id}` : ""}</h3>
                <p className="text-slate-600 text-sm mt-1">Requests sent to this mentor.</p>
              </div>
              <button onClick={closeRequestsModal} className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500">
                <FaTimes />
              </button>
            </div>

            <div className="p-6">
              {requestsLoading ? (
                <div className="py-12 text-center text-slate-600">Loading requests...</div>
              ) : requestsError ? (
                <div className="py-12 text-center text-red-600">{requestsError}</div>
              ) : mentorRequests.length === 0 ? (
                <div className="py-12 text-center text-slate-600">No requests found for this mentor.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="px-4 py-3 text-sm font-medium text-slate-600">Startup</th>
                        <th className="px-4 py-3 text-sm font-medium text-slate-600">Mail Request</th>
                        <th className="px-4 py-3 text-sm font-medium text-slate-600">Requested At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mentorRequests.map((req) => (
                        <tr key={req._id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-700">
                            {req.startup_name || req.startup?.name || req.startupName || req.startup_id || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">
                            {req.mail_request === 1 || req.mail_request === "1" ? "Yes" : "No"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {req.createdAt ? new Date(req.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-6 text-right">
                <button onClick={closeRequestsModal} className="px-6 py-3 bg-indigo-600 text-white rounded-xl">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
